/**
 * Hostnames allowed for next/image remote optimization.
 * next.config.ts `images.remotePatterns` is built from this list — add hosts in one place only.
 */
export const NEXT_IMAGE_REMOTE_HOSTNAMES = [
  'picsum.photos',
  'images.unsplash.com',
  'market.vupt.workers.dev',
  'storage.googleapis.com',
] as const;

export const REMOTE_IMAGE_DIRECT_CDN_HOSTNAMES = ['market.vupt.workers.dev'] as const;

const ALLOWED_HOSTS = new Set<string>(NEXT_IMAGE_REMOTE_HOSTNAMES);
const DIRECT_CDN_HOSTS = new Set<string>(REMOTE_IMAGE_DIRECT_CDN_HOSTNAMES);

/**
 * True when Next.js `<Image />` may load this src (same-origin paths + allowlisted HTTPS hosts).
 * `data:` URLs return false — render with a native `<img>`.
 */
export function isUrlSafeForNextImage(src: string): boolean {
  const s = src.trim();
  if (!s) return false;
  if (s.startsWith('/')) return true;
  if (s.startsWith('data:')) return false;
  try {
    const u = new URL(s);
    if (u.protocol !== 'https:' && u.protocol !== 'http:') return false;
    return ALLOWED_HOSTS.has(u.hostname);
  } catch {
    return false;
  }
}

/** True when the URL is a ready-to-render asset on our CDN (full.webp, thumb, etc.). */
export function isDirectCdnImageUrl(src: string): boolean {
  const s = src.trim();
  if (!s || s.startsWith('/') || s.startsWith('data:')) return false;
  try {
    const u = new URL(s);
    if (u.protocol !== 'https:' && u.protocol !== 'http:') return false;
    return DIRECT_CDN_HOSTS.has(u.hostname);
  } catch {
    return false;
  }
}

/** Remote http(s) URL (any host). Use before rendering an untrusted string as `<img src>`. */
export function isHttpOrHttpsImageUrl(src: string): boolean {
  const s = src.trim();
  if (!s || s.startsWith('data:')) return false;
  if (s.startsWith('/')) return true;
  try {
    const u = new URL(s);
    return u.protocol === 'https:' || u.protocol === 'http:';
  } catch {
    return false;
  }
}
